import styles from "./index.module.css";
import DocLayout from "../../components/DocLayout";
import { useLocation } from "react-router-dom";

const TermsOfService = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const ref = searchParams.get("ref");
  return (
    <DocLayout callbackUrl={ref}>
      <div className={styles.container}>
        <h1>Terms of Service (ToS)</h1>
        <p>
          <strong>QR Studio LLC</strong>
        </p>
        <p>
          <strong>Last Updated:</strong> December 1, 2025
        </p>

        <h2>1. Acceptance of Terms</h2>
        <p>
          These Terms of Service ("Terms") govern your access to and use of QR
          Studio's cloud-hosted platform and related services (collectively, the
          "Platform"). The Platform may include web-based portals,
          authentication services, analytics and reporting features, and
          connections to VR training applications (each, an "Application"). By
          creating an account, accessing, or using the Platform, you agree to be
          bound by these Terms. If you do not agree, do not use the Platform.
        </p>
        <p>
          If you are using the Platform on behalf of an educational institution,
          enterprise, or other organization ("Institution"), you represent that
          you have authority to bind that entity, and "you" refers to both you
          and the Institution. If there is a separate written agreement between
          QR Studio and the Institution (e.g., an order form, subscription
          agreement, or master services agreement), that agreement controls in
          the event of a conflict with these Terms.
        </p>

        <h2>2. Eligibility and Accounts</h2>
        <ul>
          <li>
            You must be at least 18 years old, or the age of majority in your
            jurisdiction, to register for an individual account, unless access
            is provided through an Institution as described below.
          </li>
          <li>
            Institutional accounts (colleges, schools, and enterprises) may
            authorize instructors, administrators, and learners to access the
            Platform in accordance with their subscription or license.
          </li>
          <li>
            Access for minors under the age of 18 must be provisioned and
            managed by the Institution, which is responsible for obtaining any
            required parental or guardian consents.
          </li>
          <li>
            You are responsible for maintaining the confidentiality of your
            account credentials and for all activity that occurs under your
            account.
          </li>
        </ul>

        <h2>3. Authorized Use and Acceptable Use</h2>
        <p>
          You may use the Platform solely for educational, training, and
          enterprise purposes as permitted by your Institution or by a direct
          subscription.
        </p>
        <ol type="a">
          <li>
            Use the Platform for any unlawful, harmful, or fraudulent activity.
          </li>
          <li>
            Upload or transmit malicious code (including viruses or malware) or
            content that interferes with the Platform's security or operations.
          </li>
          <li>
            Attempt to gain unauthorized access to any accounts, systems, or
            networks.
          </li>
          <li>
            Circumvent or attempt to disable any security, authentication,
            licensing, or usage limitations.
          </li>
          <li>
            Reverse engineer, decompile, or attempt to derive source code from
            the Platform, except to the extent such restriction is prohibited by
            applicable law.
          </li>
          <li>
            Use the Platform in a manner that could damage, disable, overburden,
            or impair our systems.
          </li>
        </ol>
        <p>
          <strong>VR safety.</strong> When using any VR Application in
          connection with the Platform, you must follow all safety guidance
          provided by the headset manufacturer and your Institution. You are
          responsible for ensuring a safe physical environment for VR use.
        </p>

        <h2>4. Compliance With Education and Privacy Laws</h2>
        <p>
          QR Studio is committed to complying with applicable education and
          privacy laws. Our processing of personal data is described in our{" "}
          <a
            href="https://qrstudio.ai/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          .
        </p>

        <h3>FERPA (U.S.)</h3>
        <p>
          For Institutions subject to the Family Educational Rights and Privacy
          Act, certain data processed on the Platform may constitute "education
          records". Where applicable, QR Studio acts as a "school official" with
          a "legitimate educational interest" and will use such data only to
          provide the Platform services as directed by the Institution and in
          compliance with FERPA requirements.
        </p>

        <h3>COPPA (U.S.)</h3>
        <p>
          The Platform is not intended to be used to knowingly collect personal
          information directly from children under 13. If access for children
          under 13 is permitted through an Institution, the Institution is
          responsible for providing notice and obtaining parental or guardian
          consent as required by COPPA.
        </p>

        <h3>GDPR (EU/EEA)</h3>
        <p>
          For users in the European Union or European Economic Area, QR Studio
          may process personal data as a "data processor" on behalf of the
          Institution (the "data controller"). Processing is limited to
          providing the Platform services and, where required, is governed by an
          applicable Data Processing Agreement (DPA).
        </p>

        <h3>Other jurisdictions</h3>
        <p>
          Where required, QR Studio will comply with applicable privacy and
          education data protection laws in the relevant jurisdiction.
        </p>

        <h2>5. Content, Data, and Feedback</h2>

        <h3>Institutional Data</h3>
        <p>
          Performance metrics, analytics, learning progress, and similar outputs
          made available on the Platform that relate to an Institution's users
          ("Institutional Data") are owned or controlled by the Institution,
          subject to applicable law.
        </p>

        <h3>User-Generated Content</h3>
        <p>
          If you upload, submit, or transmit content through the Platform (e.g.,
          notes, comments, files, media, or other materials) ("User Content"),
          you grant QR Studio a non-exclusive, worldwide, royalty-free license
          to host, store, reproduce, and display such User Content solely for
          the purpose of operating and improving the Platform and providing
          services to the Institution.
        </p>

        <h3>Feedback</h3>
        <p>
          If you provide suggestions, ideas, or feedback regarding the Platform
          ("Feedback"), you grant QR Studio a perpetual, irrevocable, worldwide,
          royalty-free right to use and incorporate the Feedback without
          restriction or compensation.
        </p>

        <h2>6. Service Availability and Updates</h2>
        <p>
          We strive to keep the Platform available and operational, but we do
          not guarantee uninterrupted or error-free access. QR Studio may
          modify, suspend, or discontinue parts of the Platform at its
          discretion. Updates, enhancements, and new features may be deployed
          periodically.
        </p>

        <h2>7. Fees and Subscriptions</h2>
        <p>
          Access to the Platform may be subject to subscription fees, license
          fees, or enterprise agreements between an Institution and QR Studio.
          Non-payment may result in suspension or termination of access, subject
          to any applicable written agreement.
        </p>

        <h2>8. Third-Party Services and Device Platforms</h2>
        <p>
          The Platform may integrate or interoperate with third-party services,
          cloud providers, headset ecosystems, identity providers, learning
          management systems, or other third-party products ("Third-Party
          Services"). Your use of Third-Party Services is subject to their terms
          and policies, and QR Studio is not responsible for their availability,
          content, or practices.
        </p>

        <h2>9. Intellectual Property</h2>
        <p>
          The Platform, including its software, user interface, design,
          trademarks, and documentation (excluding Institutional Data and User
          Content), is and remains the exclusive property of QR Studio and its
          licensors. No rights are granted to you except as expressly set forth
          in these Terms and any applicable written agreement.
        </p>

        <h2>10. Termination</h2>
        <p>
          QR Studio may suspend or terminate your access if you violate these
          Terms or misuse the Platform. Institutions may also control, manage,
          and revoke access for their authorized users. Upon termination, your
          right to use the Platform ceases immediately, subject to any
          applicable written agreement.
        </p>

        <h2>11. Disclaimer of Warranties</h2>
        <p>
          The Platform is provided "AS IS" and "AS AVAILABLE". To the maximum
          extent permitted by law, QR Studio disclaims all warranties, express
          or implied, including warranties of merchantability, fitness for a
          particular purpose, non-infringement, and any warranties arising out
          of course of dealing or usage of trade.
        </p>
        <p>
          <strong>Training simulations.</strong> Applications provided through
          the Platform are intended for training and educational purposes only.
          They do not provide medical, legal, or professional advice and should
          not be used as a substitute for professional judgment or applicable
          clinical standards.
        </p>

        <h2>12. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, QR Studio shall not be liable
          for any indirect, incidental, consequential, special, exemplary, or
          punitive damages arising out of or relating to your use of the
          Platform. QR Studio's total liability for any claim shall not exceed
          the fees paid for access to the Platform in the twelve (12) months
          preceding the event giving rise to the claim.
        </p>

        <h2>13. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless QR Studio and its
          affiliates, officers, directors, employees, and agents from and
          against any claims, damages, liabilities, losses, and expenses
          (including reasonable attorneys' fees) arising out of or relating to:
          (a) your use of the Platform; (b) your violation of these Terms; or
          (c) your violation of any law or the rights of any third party.
        </p>

        <h2>14. Governing Law and Dispute Resolution</h2>
        <p>
          These Terms shall be governed by the laws of the State of Wyoming,
          without regard to its conflict of laws principles, except where
          superseded by an applicable written agreement with an Institution.
        </p>
        <p>
          <strong>Dispute resolution.</strong> Unless prohibited by applicable
          law or superseded by an applicable written agreement, disputes arising
          out of or relating to these Terms shall be resolved by binding
          arbitration administered by the American Arbitration Association (AAA)
          under its Commercial Arbitration Rules. The arbitration will be
          conducted by a single arbitrator in Cheyenne, Wyoming, in English. The
          arbitrator's award may be entered in any court of competent
          jurisdiction.
        </p>
        <p>
          <strong>Waiver of jury trial and class actions.</strong> Arbitration
          will be conducted only on an individual basis. You and QR Studio waive
          any right to a jury trial or to participate in class, collective, or
          representative proceedings.
        </p>

        <h2>15. Changes to Terms</h2>
        <p>
          QR Studio may update these Terms from time to time. If we make
          material changes, we will provide notice as required by law or by an
          applicable written agreement. Continued use of the Platform after
          changes are posted constitutes acceptance of the revised Terms.
        </p>

        <h2>Contact Information</h2>
        <address>
          QR Studio LLC
          <br />
          30 North Gould Street, Ste N
          <br />
          Sheridan, WY 82801
          <br />
          United States
          <br />
          <br />
          Email: <a href="mailto:br@qrstudioai.com">br@qrstudioai.com</a>
          <br />
          Website:{" "}
          <a
            href="https://qrstudioai.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            qrstudioai.com
          </a>
        </address>
      </div>
    </DocLayout>
  );
};

export default TermsOfService;
